import { IComputedParameterDef, IParameter, TParameterKey } from '@/pages/Parameters/parameters.types'
import useDataRef, { IUseDataRefProps } from './useDataRef'
import { MutableRefObject, useCallback, useMemo, useRef } from 'react'
import { TDataWithComputedRef } from '../data.types'
import dataUtils from '../data.utils'

export interface IDataWithComputedProps extends Omit<IUseDataRefProps, 'parameters'> {
  parameters: TParameterKey[] // тут и дорасчитываемые, и обычные
  computed?: IComputedParameterDef[]
  onNewValueReceived?: () => void
}

const useDataWithComputedRef = (
  props: IDataWithComputedProps
): { dataRef: MutableRefObject<TDataWithComputedRef>; loading: boolean } => {
  const parsedToBaseParameterIds: IParameter['id'][] = useMemo(
    () => dataUtils.parsedToBaseParameterIds(props.parameters, props.computed),
    [props.parameters, props.computed]
  )

  const computedDataRef = useRef<MutableRefObject<TDataWithComputedRef>>(null)

  const recompute = useCallback(() => {
    const dataRef = computedDataRef.current
    if (!dataRef) return
    if (!props.computed || !props.computed.length) return

    for (const e of props.devices) {
      if (!dataRef.current[e]) continue
      for (const point of (dataRef.current as TDataWithComputedRef)[e]) {
        const parameterMap = Object.fromEntries(
          point.values.map((v, index) => [v.parameterId, { index, value: v.value }])
        )
        for (const c of props.computed) {
          const value = c.compute({
            equipmentId: e,
            get: (id: IParameter['id']) => parameterMap[id]?.value ?? 0, // TODO: уточнить
          })

          if (!parameterMap[c.id]) point.values.push({ parameterId: c.id, value })
          else point.values[parameterMap[c.id].index].value = value
        }
      }
    }
  }, [props.devices, props.computed])

  const handleNewValueReceived = useCallback(() => {
    recompute()
    if (props.onNewValueReceived) props.onNewValueReceived()
  }, [props.onNewValueReceived, recompute])

  const { dataRef, loading } = useDataRef({
    ...props,
    parameters: parsedToBaseParameterIds,
    onNewValueReceived: handleNewValueReceived,
  })

  computedDataRef.current = dataRef as MutableRefObject<TDataWithComputedRef>

  return {
    dataRef,
    loading,
  }
}

export default useDataWithComputedRef
